import ScrollToFirstError from 'components/form/scroll-to-first-error';
import { ConnectedPageLayout } from 'components/layout/page-layout';
import { Form, Formik } from 'formik';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RouteComponentProps, useParams } from 'react-router-dom';

import { Box, FormControlLabel, Grid, Switch, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';

import { Job, JobMatch } from '@libs/graphql-types';
import Button from '@libs/ui/components/button';
import { OptionType } from '@libs/ui/components/form/select';

import { ApplicationStatusDisplay } from './components';
import DetailsSection from './details';
// Import components from organized folders
import NameAndGeneralInfo from './general';
// Import utilities
import {
  useLoadApplicationData,
  useSaveApplication,
  useSubmitFieldGlassCandidateIntroductionMutation,
  useSubmissionStatusPolling,
} from './hooks';
import { SubmittingCandidateModal } from './modals';
import { QualificationsSection } from './qualifications';
import { QuestionsSection } from './questions';
import RateInfoSection from './rate-info';
import { ApplicationFormState } from './types';
import { createApplicationFormSchema } from './validator';

interface ApplicationFormProps extends RouteComponentProps<{
  jobId: string;
  matchId: string;
}> {}

const useStyles = makeStyles((theme) => ({
  form: {
    maxWidth: 600,
    paddingBottom: theme.spacing(4),
  },
  contentWrapper: {
    position: 'relative',
  },
  contentRoot: {
    '& .MuiStepSection-root': {
      marginBottom: theme.spacing(2),
    },
  },
  formWrapper: {
    borderRadius: '4px',
    paddingTop: theme.spacing(1),
  },
  actions: {
    marginTop: theme.spacing(4),
  },
  header: {
    backgroundColor: 'transparent',
    padding: theme.spacing(3, 4),
    marginBottom: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
  },
  jobTitle: {
    fontSize: '0.875rem',
    marginTop: theme.spacing(1),
    color: theme.palette.text.secondary,
  },
  '@global': {
    '.MuiPopover-root': {
      zIndex: 1500,
    },
  },
}));

const ApplicationForm: React.FC<ApplicationFormProps> = () => {
  const classes = useStyles();
  const { t } = useTranslation('jobForm');
  const { jobId, matchId } = useParams<{ jobId: string; matchId: string }>();
  const { initialValues, isLoading, job } = useLoadApplicationData({
    jobId,
    matchId,
  });
  const [showOnlyRequired, setShowOnlyRequired] = useState(true);

  const currentMatch = job?.matches?.find((match) => match?.id === matchId);

  const { loading: isSubmittingIntroduction, submitIntroduction } =
    useSubmitFieldGlassCandidateIntroductionMutation();

  const { loading: isSubmitting, onSubmit: submitApplication } =
    useSaveApplication(jobId, job as Job, currentMatch as JobMatch);

  // Poll submission status
  const {
    data: submissionStatusData,
    loading: isLoadingStatus,
    isNotManageable,
  } = useSubmissionStatusPolling({
    matchId,
    enabled: Boolean(matchId && currentMatch),
  });

  const handleToggleRequired = () => {
    setShowOnlyRequired(!showOnlyRequired);
  };

  const handleSubmitCandidate = async (introductionId: string) => {
    await submitIntroduction(introductionId);
  };

  const cvOptions = currentMatch?.talent?.documents?.map((doc) => ({
    value: doc!.hash,
    text: doc!.title,
  })) as OptionType[];

  if (isLoading) {
    return (
      <ConnectedPageLayout
        documentTitle={t('applicationForm.documentTitle')}
        classes={{ contentWrapper: classes.contentWrapper }}
        isLoading={true}
        drawerProps={{}}
      >
        <Box className={classes.contentRoot}>
          <Typography variant="h5" paragraph>
            Submit Candidate to VMS
          </Typography>
        </Box>
      </ConnectedPageLayout>
    );
  }

  if (!currentMatch) {
    return (
      <ConnectedPageLayout
        documentTitle={t('applicationForm.documentTitle')}
        classes={{ contentWrapper: classes.contentWrapper }}
        drawerProps={{}}
      >
        <Box className={classes.contentRoot}>
          <Typography variant="h5" paragraph>
            {t('applicationForm.matchNotFound', { matchId })}
          </Typography>
        </Box>
      </ConnectedPageLayout>
    );
  }

  const validationSchema = createApplicationFormSchema(job as Job);

  return (
    <ConnectedPageLayout
      documentTitle={t('applicationForm.documentTitle')}
      classes={{ contentWrapper: classes.contentWrapper }}
      drawerProps={{}}
    >
      <Box className={classes.contentRoot}>
        <Box className={classes.header}>
          <Typography variant="h5">
            {t('applicationForm.documentTitle')}
          </Typography>
          <Typography variant="body2" className={classes.jobTitle}>
            {t('applicationForm.forJob', {
              jobName: job?.name || 'Unknown Job',
            })}
          </Typography>
          <Box mt={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={showOnlyRequired}
                  onChange={handleToggleRequired}
                  color="info"
                  size="small"
                />
              }
              label={t('applicationForm.showRequired')}
            />
          </Box>
        </Box>

        <Box className={classes.formWrapper}>
          <Formik<ApplicationFormState>
            initialValues={initialValues}
            onSubmit={submitApplication}
            validationSchema={validationSchema}
            validateOnChange={false}
            validateOnBlur={true}
          >
            {({ isValid, dirty, values, errors }) => {
              console.log('🚀 ~ errors:', errors);
              console.log('🚀 ~ values:', values);

              return (
                <Box className={classes.form}>
                  <Form>
                    <ScrollToFirstError />

                    <NameAndGeneralInfo
                      showOnlyRequired={showOnlyRequired}
                      cvOptions={cvOptions}
                    />
                    <DetailsSection
                      showOnlyRequired={showOnlyRequired}
                      job={job as Job}
                    />
                    <QuestionsSection
                      job={job as Job}
                      sectionName="questions"
                      index={3}
                      showOnlyRequired={showOnlyRequired}
                    />
                    <QualificationsSection
                      job={job as Job}
                      sectionName="qualifications"
                      index={4}
                      showOnlyRequired={showOnlyRequired}
                    />
                    <RateInfoSection
                      showOnlyRequired={showOnlyRequired}
                      job={job as Job}
                    />

                    {/* Application Status Display */}
                    <ApplicationStatusDisplay
                      data={submissionStatusData}
                      loading={isLoadingStatus}
                    />

                    <Grid spacing={4} container className={classes.actions}>
                      <Grid item>
                        <Button
                          type="submit"
                          variant="contained"
                          color="info"
                          size="large"
                          disabled={
                            isSubmitting ||
                            !values.introductionId ||
                            isSubmittingIntroduction ||
                            isNotManageable
                          }
                          onClick={() => {
                            handleSubmitCandidate(values.introductionId!);
                          }}
                        >
                          {t('applicationForm.submitCandidate')}
                        </Button>
                      </Grid>
                      <Grid item>
                        <Button
                          type="submit"
                          variant={
                            values?.introductionId ? 'outlined' : 'contained'
                          }
                          color="info"
                          size="large"
                          disabled={
                            isSubmittingIntroduction ||
                            isSubmitting ||
                            (!isValid && dirty) ||
                            isNotManageable
                          }
                        >
                          {t('applicationForm.saveCandidate')}
                        </Button>
                      </Grid>
                    </Grid>
                  </Form>
                </Box>
              );
            }}
          </Formik>
        </Box>
        <SubmittingCandidateModal jobId={jobId} />
      </Box>
    </ConnectedPageLayout>
  );
};

export default ApplicationForm;
